import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AuthLayout from "../../components/AuthLayout";
import { AuthAPI } from "../../api/authAPI";
import { useAuth } from "../../context/AuthContext";
import {
  APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
  APPROVED_USER_STATUS,
  ONBOARDING_SUPPORT_EMAIL_ID,
  ONBOARDING_USER_STATUS,
  USER_IN_REVIEW,
  USER_IS_WAITLISTED,
} from "../../utils/constants";

const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 3 * 60 * 1000;

const STATUS_COPY = {
  in_review: {
    title: (brandName) => `${brandName} is in review`,
    body: "Thanks for showing interest in Wishlink. Our team is reviewing your application to ensure the best experience for you. ",
    showInfoCard: true,
  },
  rejected: {
    title: (brandName) => `${brandName} has been rejected`,
    body: "Oops! After careful consideration, we have decided to add you to our onboarding waitlist for now.",
    showInfoCard: false,
  },
};

export default function ReviewStatusScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout, emailUserAuthToken, adminAuthToken } = useAuth();
  const [pollWindowOpen, setPollWindowOpen] = useState(
    Boolean(location.state?.justSubmitted),
  );

  const brandName =
    user?.data?.brand?.display_name ||
    user?.data?.parent_brand?.name ||
    "Your brand";
  const userEmail = user?.data?.email || "";
  const userStatus = user?.data?.status;
  const variant = userStatus === USER_IS_WAITLISTED ? "rejected" : "in_review";
  const copy = STATUS_COPY[variant];

  useEffect(() => {
    if (!emailUserAuthToken && !adminAuthToken) {
      navigate("/login", { replace: true });
      return;
    }
    if (!userStatus) return;
    if (
      userStatus === APPROVED_USER_STATUS ||
      userStatus === APPROVED_THROUGH_DIY_FLOW_USER_STATUS
    ) {
      navigate("/dashboard/home", { replace: true });
      return;
    }
    if (userStatus === ONBOARDING_USER_STATUS) {
      navigate("/register", { replace: true });
    }
  }, [navigate, userStatus, emailUserAuthToken, adminAuthToken]);

  const isInReview = userStatus === USER_IN_REVIEW;

  // Polls the light verification-status endpoint, not /me. A MANUAL_REVIEW or ERROR verdict
  // leaves the user in review, so status alone cannot end the wait.
  const verification = useQuery({
    queryKey: ["onboardingVerificationStatus"],
    queryFn: AuthAPI.getOnboardingVerificationStatus,
    enabled: isInReview && pollWindowOpen,
    refetchInterval: (query) =>
      query.state.data?.data?.pending ? POLL_INTERVAL_MS : false,
    retry: false,
  });
  const verificationStatus = verification.data?.data?.status;
  const isChecking =
    isInReview &&
    pollWindowOpen &&
    (verification.isPending || verification.data?.data?.pending === true);

  useEffect(() => {
    const timeout = setTimeout(() => setPollWindowOpen(false), POLL_WINDOW_MS);
    return () => clearTimeout(timeout);
  }, []);

  // The agent approved or rejected: refresh the user once so the redirect / copy updates.
  useEffect(() => {
    if (verificationStatus && verificationStatus !== USER_IN_REVIEW) {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    }
  }, [verificationStatus, queryClient]);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <AuthLayout
      footer={
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <Typography
            component="span"
            sx={{ color: "#808080", fontSize: 14, lineHeight: "20px" }}
          >
            For any questions - please reach out to us at
          </Typography>
          <Box
            component="a"
            href={`mailto:${ONBOARDING_SUPPORT_EMAIL_ID}`}
            sx={{
              color: "#ee4d37",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "20px",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {ONBOARDING_SUPPORT_EMAIL_ID}
          </Box>
        </Box>
      }
    >
      <Box sx={{ width: "100%", mt: "46px" }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Typography
              sx={{ fontSize: 24, fontWeight: 600, lineHeight: "28px" }}
            >
              {copy.title(brandName)}
            </Typography>
            <Typography
              sx={{
                color: "#808080",
                fontSize: 14,
                lineHeight: "20px",
                maxWidth: 478,
                mx: "auto",
              }}
            >
              {copy.body}
            </Typography>
          </Stack>

          {copy.showInfoCard && isChecking && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#808080",
              }}
            >
              <CircularProgress size={16} sx={{ color: "#ee4d37" }} />
              <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                We&apos;re checking your details. This usually takes 2-3
                minutes.
              </Typography>
            </Box>
          )}

          {copy.showInfoCard && isInReview && !isChecking && (
            <Box
              sx={{
                width: "100%",
                maxWidth: 478,
                p: "20px",
                borderRadius: "8px",
                textAlign: "left",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.5) 100%), linear-gradient(90deg, rgb(194,208,242) 0%, rgb(194,208,242) 100%)",
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#144cc7",
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}
                  >
                    You will hear from us in 4 to 5 business days
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: "#0d0d0d",
                      fontWeight: 500,
                    }}
                  >
                    Once approved, you&apos;ll be able to access your brand
                    dashboard and start collaborating with creators.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: "#0d0d0d",
                      fontWeight: 500,
                    }}
                  >
                    Log back in anytime to check your status.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#808080",
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 14, lineHeight: "20px" }}
            >
              You are signed in as
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#0d0d0d",
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "20px",
              }}
            >
              {userEmail}
            </Typography>
            <Typography
              component="span"
              sx={{ color: "#d1d1d1", fontSize: 12 }}
            >
              |
            </Typography>
            <Button
              onClick={onLogout}
              sx={{
                minWidth: "auto",
                p: 0,
                color: "#ee4d37",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              Log Out
            </Button>
          </Box>
        </Stack>
      </Box>
    </AuthLayout>
  );
}
